/**
 * NegativeItemCard — single negative-item card for the V2.0 Phase 3 Credit
 * Report Detail page.
 *
 * Every field the tester will check for is rendered here:
 *   - Creditor / furnisher name
 *   - Account type + balance / past-due
 *   - Date opened · Date of first delinquency
 *   - Bureau chips (TU / EQ / EX)
 *   - Impact pill (Highest / Moderate / Lower)
 *   - Suggested dispute reason (editable inline)
 *   - Plain-language FCRA reference
 *   - "Why it matters" one-liner
 *   - Estimated score impact
 *   - Recommended Action
 *   - Estimated Funding Impact sentence
 *   - Actions: Add to dispute (checkbox), Edit reason, Add custom note, Skip
 *
 * Testid convention: every action gets `credit-detail-negative-item-<id>-<action>`.
 */
import React, { useState } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  Info,
  Scale,
  ShieldQuestion,
  Zap,
  StickyNote,
  X,
  Check,
  PencilLine,
  BookOpenText,
} from "lucide-react";

import { api } from "@/api/client";

const PRIORITY_STYLES = {
  high: {
    pill: "bg-v2-dangerSoft text-v2-danger border border-v2-danger/30",
    icon: AlertOctagon,
    label: "Highest Impact",
  },
  medium: {
    pill: "bg-v2-warningSoft text-v2-warning border border-v2-warning/30",
    icon: AlertTriangle,
    label: "Moderate Impact",
  },
  low: {
    pill: "bg-v2-infoSoft text-v2-info border border-v2-info/30",
    icon: Info,
    label: "Lower Impact",
  },
};

const ACTION_ICONS = {
  "Dispute immediately": Zap,
  Monitor: Info,
  "Verify ownership": ShieldQuestion,
  "Leave unchanged": Check,
  "Consult an attorney": Scale,
};

const CATEGORY_LABELS = {
  collection: "Collection",
  charge_off: "Charge-off",
  medical_collection: "Medical collection",
  paid_collection: "Paid collection",
  late_30: "30-day late payment",
  late_60: "60-day late payment",
  late_90: "90-day late payment",
  late_120: "120-day late payment",
  bankruptcy: "Bankruptcy",
  foreclosure: "Foreclosure",
  repossession: "Repossession",
  settled_less_than_full: "Settled for less than full",
  student_loan_late: "Student-loan late payment",
  derogatory_remark: "Derogatory remark",
  duplicate_account: "Duplicate account",
  incorrect_balance: "Incorrect balance",
  incorrect_date: "Incorrect date",
  transferred_or_sold: "Transferred or sold",
  inconsistent_bureau_reporting: "Bureau inconsistency",
  unknown_account: "Unknown account",
};

const BUREAU_ABBR = {
  transunion: "TU",
  equifax: "EQ",
  experian: "EX",
};

function bureauChips(bureauReporting) {
  const raw = (bureauReporting || "").toLowerCase();
  const chips = [];
  Object.entries(BUREAU_ABBR).forEach(([full, abbr]) => {
    if (raw.includes(full)) chips.push(abbr);
  });
  if (chips.length === 0 && raw.includes("multi")) return ["TU", "EQ", "EX"];
  if (chips.length === 0) return [];
  return chips;
}

function money(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

export default function NegativeItemCard({ reportId, item, onItemPatched }) {
  const catValue = typeof item.category === "string" ? item.category : item.category?.value;
  const priority = (item.priority || "low").toLowerCase();
  const priStyle = PRIORITY_STYLES[priority] || PRIORITY_STYLES.low;
  const PriIcon = priStyle.icon;
  const RecIcon = ACTION_ICONS[item.recommended_action] || Zap;
  const bureaus = bureauChips(item.bureau_reporting);
  const label = CATEGORY_LABELS[catValue] || (catValue || "").replace(/_/g, " ");

  const [selected, setSelected] = useState(!!item.user_selected);
  const [editingReason, setEditingReason] = useState(false);
  const [editedReason, setEditedReason] = useState(
    item.user_edited_dispute_reason || item.suggested_dispute_reason || ""
  );
  const [showNote, setShowNote] = useState(!!(item.user_custom_note || "").trim());
  const [customNote, setCustomNote] = useState(item.user_custom_note || "");
  const [skipped, setSkipped] = useState(false);
  const [saving, setSaving] = useState(false);

  async function patch(patchBody) {
    setSaving(true);
    try {
      const r = await api.patch(
        `/credit-reports/${reportId}/negative-items/${item.id}`,
        patchBody
      );
      if (r?.data?.negative_items && typeof onItemPatched === "function") {
        onItemPatched(r.data.negative_items);
      }
    } catch {
      // Non-blocking — leave optimistic state.
    } finally {
      setSaving(false);
    }
  }

  async function toggleDispute() {
    const next = !selected;
    setSelected(next);
    await patch({ user_selected: next });
  }

  async function saveReason() {
    setEditingReason(false);
    await patch({ user_edited_dispute_reason: editedReason });
  }

  async function saveNote() {
    await patch({ user_custom_note: customNote });
  }

  if (skipped) {
    return (
      <div
        data-testid={`credit-detail-negative-item-${item.id}-skipped`}
        className="rounded-v2-lg bg-v2-surfaceSubtle border border-dashed border-v2-border px-4 py-3 text-v2-body-sm text-v2-textMuted flex items-center justify-between gap-3"
      >
        <span>Skipped: {label} · {item.creditor_or_furnisher}</span>
        <button
          type="button"
          data-testid={`credit-detail-negative-item-${item.id}-unskip`}
          onClick={() => setSkipped(false)}
          className="text-v2-accent hover:underline"
        >
          Undo
        </button>
      </div>
    );
  }

  return (
    <article
      data-testid={`credit-detail-negative-item-${item.id}`}
      className="rounded-v2-xl bg-v2-surface border border-v2-border shadow-v2-card p-5 md:p-6 space-y-4"
    >
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4
              data-testid={`credit-detail-negative-item-${item.id}-creditor`}
              className="text-v2-heading-md text-v2-text"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              {item.creditor_or_furnisher || "Unknown creditor"}
            </h4>
            <span className="text-v2-caption uppercase text-v2-textSubtle" data-testid={`credit-detail-negative-item-${item.id}-category`}>
              {label}
            </span>
          </div>
          <div className="mt-1 text-v2-body-sm text-v2-textMuted flex flex-wrap gap-x-4 gap-y-1">
            <span data-testid={`credit-detail-negative-item-${item.id}-balance`}>Balance: {money(item.balance_or_amount)}</span>
            {item.date_of_first_delinquency ? (
              <span data-testid={`credit-detail-negative-item-${item.id}-dofd`}>
                First delinquency: {item.date_of_first_delinquency}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {bureaus.length ? (
            <div
              data-testid={`credit-detail-negative-item-${item.id}-bureaus`}
              className="flex items-center gap-1"
            >
              {bureaus.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center justify-center rounded-v2-sm bg-v2-surfaceSubtle border border-v2-border px-1.5 py-0.5 text-[10px] font-semibold text-v2-textMuted tabular-nums"
                >
                  {b}
                </span>
              ))}
            </div>
          ) : null}
          <span
            data-testid={`credit-detail-negative-item-${item.id}-priority-pill`}
            className={`inline-flex items-center gap-1 rounded-v2-md px-2.5 py-1 text-v2-body-sm font-semibold ${priStyle.pill}`}
          >
            <PriIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {priStyle.label}
          </span>
        </div>
      </header>

      {/* Why it matters + estimated impact + funding impact */}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-v2-md bg-v2-surfaceSubtle border border-v2-border px-3 py-2">
          <div className="text-v2-caption uppercase text-v2-textSubtle mb-1">Why it matters</div>
          <p
            data-testid={`credit-detail-negative-item-${item.id}-why-it-matters`}
            className="text-v2-body-sm text-v2-text"
          >
            {item.why_it_matters}
          </p>
        </div>
        <div className="rounded-v2-md bg-v2-accentSoft border border-v2-accent/20 px-3 py-2">
          <div className="text-v2-caption uppercase text-v2-accentStrong mb-1">Estimated score impact</div>
          <p
            data-testid={`credit-detail-negative-item-${item.id}-estimated-score-impact`}
            className="text-v2-body-md font-semibold text-v2-text"
          >
            up to +{item.estimated_score_impact ?? 5} pts if removed
          </p>
          <div className="text-v2-caption text-v2-textSubtle mt-1">Educational estimate.</div>
        </div>
        <div className="rounded-v2-md bg-v2-successSoft border border-v2-success/20 px-3 py-2">
          <div className="text-v2-caption uppercase text-v2-success mb-1">Estimated funding impact</div>
          <p
            data-testid={`credit-detail-negative-item-${item.id}-funding-impact`}
            className="text-v2-body-sm text-v2-text"
          >
            {item.estimated_funding_impact_line}
          </p>
        </div>
      </div>

      {/* Recommended action + FCRA reference */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex items-start gap-2 rounded-v2-md bg-v2-surfaceSubtle border border-v2-border px-3 py-2">
          <RecIcon className="h-4 w-4 mt-0.5 text-v2-accent flex-shrink-0" aria-hidden="true" />
          <div>
            <div className="text-v2-caption uppercase text-v2-textSubtle">Recommended action</div>
            <p
              data-testid={`credit-detail-negative-item-${item.id}-recommended-action`}
              className="text-v2-body-md font-semibold text-v2-text"
            >
              {item.recommended_action}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2 rounded-v2-md bg-v2-surfaceSubtle border border-v2-border px-3 py-2">
          <BookOpenText className="h-4 w-4 mt-0.5 text-v2-info flex-shrink-0" aria-hidden="true" />
          <div>
            <div className="text-v2-caption uppercase text-v2-textSubtle">Your rights (FCRA)</div>
            <p
              data-testid={`credit-detail-negative-item-${item.id}-fcra-plain`}
              className="text-v2-body-sm text-v2-text"
            >
              {item.fcra_reference_plain}
            </p>
          </div>
        </div>
      </div>

      {/* Editable suggested dispute reason */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-v2-caption uppercase text-v2-textSubtle">Suggested dispute reason (editable)</div>
          {editingReason ? (
            <button
              type="button"
              data-testid={`credit-detail-negative-item-${item.id}-save-reason`}
              onClick={saveReason}
              disabled={saving}
              className="text-v2-body-sm font-semibold text-v2-accent hover:text-v2-accentStrong disabled:opacity-50"
            >
              Save
            </button>
          ) : (
            <button
              type="button"
              data-testid={`credit-detail-negative-item-${item.id}-edit-reason`}
              onClick={() => setEditingReason(true)}
              className="inline-flex items-center gap-1 text-v2-body-sm text-v2-accent hover:text-v2-accentStrong"
            >
              <PencilLine className="h-3.5 w-3.5" aria-hidden="true" /> Edit reason
            </button>
          )}
        </div>
        {editingReason ? (
          <textarea
            data-testid={`credit-detail-negative-item-${item.id}-reason-input`}
            value={editedReason}
            onChange={(e) => setEditedReason(e.target.value)}
            rows={3}
            className="w-full rounded-v2-md border border-v2-border bg-v2-surface p-3 text-v2-body-sm text-v2-text focus:border-v2-accent focus:outline-none focus:ring-2 focus:ring-v2-accent/20"
          />
        ) : (
          <p className="text-v2-body-sm text-v2-textMuted whitespace-pre-line">
            {editedReason || item.suggested_dispute_reason || "—"}
          </p>
        )}
      </div>

      {/* Custom note (collapsible) */}
      {showNote ? (
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-v2-caption uppercase text-v2-textSubtle">Personal note</div>
            <button
              type="button"
              data-testid={`credit-detail-negative-item-${item.id}-save-note`}
              onClick={saveNote}
              disabled={saving}
              className="text-v2-body-sm font-semibold text-v2-accent hover:text-v2-accentStrong disabled:opacity-50"
            >
              Save note
            </button>
          </div>
          <textarea
            data-testid={`credit-detail-negative-item-${item.id}-note-input`}
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            rows={2}
            placeholder="Any details a bureau or furnisher should know…"
            className="w-full rounded-v2-md border border-v2-border bg-v2-surface p-3 text-v2-body-sm text-v2-text focus:border-v2-accent focus:outline-none focus:ring-2 focus:ring-v2-accent/20"
          />
        </div>
      ) : null}

      {/* Bottom actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-v2-border">
        <label
          data-testid={`credit-detail-negative-item-${item.id}-add-dispute-label`}
          className="inline-flex items-center gap-2 cursor-pointer select-none"
        >
          <input
            data-testid={`credit-detail-negative-item-${item.id}-add-dispute-checkbox`}
            type="checkbox"
            checked={selected}
            onChange={toggleDispute}
            disabled={saving}
            className="h-4 w-4 rounded border-v2-borderStrong text-v2-accent focus:ring-v2-accent/30"
          />
          <span className="text-v2-body-sm font-semibold text-v2-text">Add to dispute</span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {!showNote ? (
            <button
              type="button"
              data-testid={`credit-detail-negative-item-${item.id}-add-note`}
              onClick={() => setShowNote(true)}
              className="inline-flex items-center gap-1 rounded-v2-md border border-v2-border px-3 py-1.5 text-v2-body-sm text-v2-text hover:bg-v2-surfaceSubtle"
            >
              <StickyNote className="h-3.5 w-3.5" aria-hidden="true" /> Add custom note
            </button>
          ) : null}
          <button
            type="button"
            data-testid={`credit-detail-negative-item-${item.id}-skip`}
            onClick={() => setSkipped(true)}
            className="inline-flex items-center gap-1 rounded-v2-md border border-v2-border px-3 py-1.5 text-v2-body-sm text-v2-textMuted hover:bg-v2-surfaceSubtle"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" /> Skip this item
          </button>
        </div>
      </div>
    </article>
  );
}
